import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { Employer } from "../models/employer";
import { of } from "rxjs";
import { ContentCard } from "../models/content-card";
import { User } from "../models/user";
import { Interaction } from "../models/interaction";
import { ReportData } from "../models/report-data";
import { DocTag, Document } from "../models/document";
import { Template } from "../models/communication/template";
import { Campaign, DripCampaignEditContext, DripProgram, DripProgramCopyRequest, DripProgramDetails } from "../models/communication/campaign";
import { EmailHtmlImportResult, EmailHtmlIssue } from "../models/communication/email-html-import";
import { InboxPreviewState } from "../models/communication/inbox-preview";
import { map } from "rxjs/operators";

@Injectable()
export class DataService {

	constructor(private apiService: ApiService) {}

	//#region USER
	getEmployerIdFromPath(path: string) {
		return this.apiService.get<string>(`employers/${path}/employerID`);
	}
	getEmployer(employerID: string) {
		return this.apiService.get<Employer>(`employers/${employerID}`).pipe(map(e => new Employer({ 
			...e, 
			dripStartDate: e.dripStartDate ? new Date(e.dripStartDate + 'Z') : null,
			smsOptInStart: e.smsOptInStart ? new Date(e.smsOptInStart + 'Z') : null 
		})));
	}
	
	//endregion

	//#region ADMIN

	saveEmployerConfig(employerID: string, employerName: string, path: string, logoFileUploadID: string, dripStartDate?: Date, deliveryTypeID?: number, dripProgramID?: string, smsOptInStart?: Date) {
		return this.apiService.post<Employer>(`employers/${employerID}`, { employerName, path, logoFileUploadID, dripStartDate, deliveryTypeID, dripProgramID, smsOptInStart });
	}

	uploadFile(uploadID: string, uploadFile: File) {
		const formData: FormData = new FormData();
		formData.append('uploadId', uploadID);
		formData.append(`uploadFile`, uploadFile, uploadFile.name);
		return this.apiService.postBlob(`file`, formData)
	}

	uploadCensusData(employerID: string, uploadFile: File) {
		const formData: FormData = new FormData();
		formData.append(`file`, uploadFile, uploadFile.name);
		return this.apiService.postBlob(`employers/${employerID}/censusdata`, formData)
	}

	downloadCensusData(employerID: string) {
		return this.apiService.getFile(`employers/${employerID}/censusdata`);
	}
	getAllCards(type: number, employerID: string = '') {
		return this.apiService.get<Array<ContentCard>>(`cards/?cardContainerID=${type}${employerID ? `&employerID=${employerID}` : ''}`);
	}

	saveEmployerCards(employerID: string, cardContainerID: number, cards: Array<{cardID: string, sortOrder: number}>) {
		return this.apiService.post(`employers/${employerID}/cards/${cardContainerID}`, cards);
	}

	saveEmployerCard(card: ContentCard) {
		let { resetCard, ...data } = card;
		return this.apiService.post(`cards/${card.cardID}`, data);
	}
	saveEmployerContent(employerID: string, content: Array<{ contentID: string, textContent: string, fileUploadID: string }>) {
		return this.apiService.post(`employers/${employerID}/content`, content);
	}

	copyEmployer(employerID: string, newEmployerID: string, newEmployerName: string, newEmployerPath: string) {
		return this.apiService.post(`employers/${employerID}/copy/${newEmployerID}`, { newEmployerName, newEmployerPath });
	}
	deleteEmployer(employerID: string) {
		return this.apiService.delete(`employers/${employerID}`);
	}
	getUsers() {
		return this.apiService.get<Array<User>>(`users`);
	}
	getUser(userID: string) {
		return this.apiService.get<User>(`users/${userID}`);
	}
	saveUser(user: User) {
		return this.apiService.post(`users/${user.userID}`, user);
	}
	deleteUser(userID: string) {
		return this.apiService.delete(`users/${userID}`);
	}

	runReport(reportData: ReportData) {
		return this.apiService.post(`report`, reportData, null, 'arraybuffer');
	}
	runCommReport(reportData: ReportData) {
		return this.apiService.post(`reportcomm`, reportData, null, 'arraybuffer');
	}
	runPresentation(data: { employerID: string, quarter: number, year: number }) {
		return this.apiService.post(`presentation`, data, null, 'arraybuffer');
	}

	deleteCard(cardID: string) {	
		return this.apiService.delete(`cards/${cardID}`);
	}

	sortCards(cardContainerID: number, cards: Array<{cardID: string, sortOrder: number}>) {
		const payload = Object.fromEntries(cards.map(c => [c.cardID, c.sortOrder]));
		return this.apiService.post(`cards/sort?cardContainerID=${cardContainerID}`, payload);
	}
	getDocuments(employerID?: string) {
		if(employerID) {
			return this.apiService.get<Array<Document>>(`employers/${employerID}/docs`);
		} else {
			return this.apiService.get<Array<Document>>(`docs`);
		}
	}
	saveDocument(doc: Document) {
		return this.apiService.post(`docs/${doc.docID}`, doc);
	}
	saveEmployerDocuments(employerID: string, docs: Array<Document>) {
		const payload = Object.fromEntries(docs.map(d => [d.docID, d.sortOrder]));
		return this.apiService.post(`employers/${employerID}/docs`, payload);
	}
	sortDocuments(docs: Array<{docID: string, sortOrder: number}>) {
		const payload = Object.fromEntries(docs.map(d => [d.docID, d.sortOrder]));
		return this.apiService.post(`docs/sort`, payload);
	}
	deleteDocument(docID: string) {
		return this.apiService.delete(`docs/${docID}`);
	}
	getTags() {
		return this.apiService.get<Array<DocTag>>(`tags`);
	}
	saveTag(tag: { tagID: string, tagDesc: string }) {
		return this.apiService.post(`tags/${tag.tagID}`, tag.tagDesc);
	}
	deleteTag(tagID: string) {
		return this.apiService.delete(`tags/${tagID}`);
	}
	//endregion

	//#region Login
	login(email: string, password: string) {
		return this.apiService.post<string>(`login`, { email, password }, null, "text").pipe(
			map(response => {
				// Remove extra quotes if they exist
				return response.replace(/^"(.*)"$/, '$1');
			})
		);
	}

	verify2FA(userID: string, code: string) {
		return this.apiService.post<string>(`2fa`, { userID, code }, null, "text");
	}

	//endregion

	//#region Employers
	getEmployers() {
		return this.apiService.get<Array<Employer>>(`employers`);
	}

	logInteraction(interaction: Interaction) {
		return this.apiService.post(`engagement`, interaction);
	}

	generateEmployerDocs(employerID: string) {
		return this.apiService.post(`employers/${employerID}/docgen`, null);
	}

	//endregion


	//#region Communication
	getCampaigns(employerID?: string, dateRange?: number, emailType?: number) {
		let params = [];
		if (employerID) params.push(`employerID=${employerID}`);
		if (dateRange) params.push(`pastDays=${dateRange}`);
		if (emailType) params.push(`emailType=${emailType}`);
		if (params.length > 0) {
			return this.apiService.get<Array<Campaign>>(`campaigns?${params.join('&')}`);
		}
		// If no params, return all campaigns
		return this.apiService.get<Array<Campaign>>(`campaigns`);
	}
	getAllCampaigns() {
		return this.apiService.get<Array<Campaign>>(`campaigns`);
	}
	getCampaign(campaignID: string) {
		return this.apiService.get<Campaign>(`campaigns/${campaignID}`).pipe(map(c => new Campaign({ ...c, deliveryDateTime: c.deliveryDateTime ? new Date(c.deliveryDateTime + 'Z') : null })));
	}
	saveCampaign(campaign: Campaign) {
		return this.apiService.post(`campaigns`, campaign);
	}
	deleteCampaign(campaignID: string) {
		return this.apiService.delete(`campaigns/${campaignID}`);
	}
	testCampaign(campaignID: string, emailAddress: string, phone: string, employerID: string) {
		return this.apiService.post(`campaigns/test/${campaignID}`, { emailAddress, phone, employerID });
	}
	// Inbox previews (Mailgun Inspect). GET is free; POST starts a run and uses one preview per mail app.
	getInboxPreview(campaignID: string) {
		return this.apiService.get<InboxPreviewState>(`inboxpreview/${campaignID}`);
	}
	runInboxPreview(campaignID: string, employerID: string) {
		return this.apiService.post<InboxPreviewState>(`inboxpreview/${campaignID}`, { employerID });
	}
	executeCampaign(campaignID: string) {
		return this.apiService.post(`campaigns/execute/${campaignID}`, null);	
	}
	getDeliveryTypes() {
		return this.apiService.get<Array<{ deliveryTypeID: number, deliveryTypeDesc: string }>>(`campaigns/deliverytypes`);
	}
	getEmailTemplateFields() {
		return this.apiService.get<any>(`campaigns/emailtemplatefields`);
	}
	getEmailHtml(campaignID: string, employerID: string) {
		return this.apiService.getHtml(`campaigns/emailhtml/${campaignID}/${employerID}`);
	}
	// Expert mode: normalize + re-host images + lint. Call on paste / drop / file open.
	importEmailHtml(html: string) {
		return this.apiService.post<EmailHtmlImportResult>(`campaigns/import-html`, { html });
	}
	// Expert mode: lint only, cheap enough to call (debounced) while typing.
	lintEmailHtml(html: string) {
		return this.apiService.post<Array<EmailHtmlIssue>>(`campaigns/lint-html`, { html });
	}

	getTemplates() {
		return this.apiService.get<Array<Template>>(`templates`);
	}
	getTemplate(templateID: string) {
		return this.apiService.get<Template>(`templates/${templateID}`);
	}
	saveTemplate(template: Template) {
		return this.apiService.post(`templates`, template);
	}
	deleteTemplate(templateID: string) {
		return this.apiService.delete(`templates/${templateID}`);
	}

	// Drip Program methods
	getDripPrograms() {
		// New endpoint returns CommDripProgram with dripProgramDesc and TimeSpan deliveryTime
		return this.apiService.get<Array<any>>(`campaigns/dripprograms`).pipe(
			map(list => list.map(dp => new DripProgram({
				...dp,
				// Normalize property naming for UI compatibility
				dripProgramName: dp.dripProgramName ?? dp.dripProgramDesc,
				dripProgramDesc: dp.dripProgramDesc ?? dp.dripProgramName,
				startDate: dp.startDate ? new Date(dp.startDate) : null,
				// Convert TimeSpan to HH:mm:ss string when needed
				deliveryTime: this.normalizeTimeSpan(dp.deliveryTime)
			})))
		);
	}
	getDripProgram(dripProgramID: string) {
		return this.apiService.get<DripProgram>(`campaigns/dripprogram/${dripProgramID}`).pipe(
			map(dp => new DripProgram({ 
				...dp, 
				startDate: dp.startDate ? new Date(dp.startDate) : null,
				deliveryTime: dp.deliveryTime ? this.convertTimeFromUTC(dp.deliveryTime) : null
			}))
		);
	}
	saveDripProgram(dripProgram: DripProgram) {
		// Convert to UTC before saving and map field names to server shape
		const dripProgramToSave: any = { 
			...dripProgram,
			dripProgramDesc: (dripProgram as any).dripProgramDesc ?? (dripProgram as any).dripProgramName,
			deliveryTime: dripProgram.deliveryTime ? this.convertTimeToUTC(dripProgram.deliveryTime) : null
		};
		// Remove client-only alias to avoid confusion
		delete dripProgramToSave.dripProgramName;
		return this.apiService.post(`campaigns/dripprogram`, dripProgramToSave);
	}
	getDripProgramDetails(dripProgramID: string) {
		return this.apiService.get<Array<DripProgramDetails>>(`campaigns/dripprogram/${dripProgramID}/details`);
	}
	saveDripProgramDetails(dripProgramID: string, details: Array<DripProgramDetails>) {
		return this.apiService.post(`campaigns/dripprogram/${dripProgramID}/details`, details);
	}
	getDefaultDripProgramConfig() {
		return this.apiService.get<{defaultDripProgramID: string}>(`campaigns/dripprogram/default`);
	}

	deleteDripProgram(dripProgramID: string) {
		return this.apiService.delete(`campaigns/dripprogram/${dripProgramID}`);
	}
	copyDripProgram(sourceId: string, req: DripProgramCopyRequest) {
		return this.apiService.post<{ dripProgramID: string }>(`campaigns/dripprogram/${sourceId}/copy`, req);
	}
	getDripCampaignEditContext(dripProgramID: string, campaignID: string) {
		return this.apiService.get<DripCampaignEditContext>(`campaigns/dripprogram/${dripProgramID}/campaign/${campaignID}/context`);
	}
	cloneDripCampaign(dripProgramID: string, sourceCampaignID: string, campaign: Campaign) {
		return this.apiService.post<{ campaignID: string, missingEmployers: any[] }>(`campaigns/dripprogram/${dripProgramID}/campaign/${sourceCampaignID}/clone`, campaign);
	}

	//endregion

	public unsubscribe(email: string, sid: string) {
		return this.apiService.post(`commwebhook/emailunsubscribe/${email}/${sid}`, {});
	}

	getSocialMediaLinks() {
		return this.apiService.get<any>(`sitedata/socialmedia`);
	}

    /**
     * Retrieves an SSO link from the server using POST
     * @returns An Observable containing the SSO link URL as a string
     */
    getSsoLink() {
        // Use POST with responseType "text" similar to the login method
        return this.apiService.post<string>(`radiansql/ssolink`, {}, null, "text");
    }

	private convertTimeToUTC(timeString: string): string {
        // Parse the time string (assuming format "HH:mm:ss")
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        
        // Create a date object with today's date and the time in local timezone
        const today = new Date();
        const localDateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds || 0);
        
        // Convert to UTC and extract time components
        const utcHours = localDateTime.getUTCHours().toString().padStart(2, '0');
        const utcMinutes = localDateTime.getUTCMinutes().toString().padStart(2, '0');
		const utcSeconds = localDateTime.getUTCSeconds().toString().padStart(2, '0');
        
        return `${utcHours}:${utcMinutes}:${utcSeconds}`;
    }

	private normalizeTimeSpan(value: any): string | null {
		if (value == null) return null;
		// If already string like "HH:mm:ss"
		if (typeof value === 'string') {
			return value.length <= 8 ? value : value.substring(0,8);
		}
		// If object with ticks property
		if (typeof value === 'object' && typeof value.ticks === 'number') {
			const totalSeconds = Math.floor(value.ticks / 10000000); // ticks to seconds
			const hours = Math.floor(totalSeconds / 3600) % 24;
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;
			return `${hours.toString().padStart(2,'0')}:${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}`;
		}
		return null;
	}

    private convertTimeFromUTC(timeString: string): string {
        // Parse the UTC time string (assuming format "HH:mm:ss")
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        
        // Create a date object with today's date and the time in UTC
        const today = new Date();
        const utcDateTime = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds || 0));
        
        // Convert to local time and extract time components
        const localHours = utcDateTime.getHours().toString().padStart(2, '0');
        const localMinutes = utcDateTime.getMinutes().toString().padStart(2, '0');
        const localSeconds = utcDateTime.getSeconds().toString().padStart(2, '0');
        
        return `${localHours}:${localMinutes}:${localSeconds}`;
    }

	/**
	 * Translates page content using the backend translation API
	 * @param employerId The employer's GUID
	 * @param fromLanguage Source language code (default: 'en')
	 * @param toLanguage Target language code (es, zh-Hans, ar, de)
	 * @param pagePath The page identifier (e.g., '/home', '/learn-more')
	 * @param items Array of objects with id and text to translate
	 * @returns Observable containing snapshotHash and translations object
	 */
	translatePage(employerId: string, fromLanguage: string, toLanguage: string, pagePath: string, items: Array<{ id: string; text: string }>) {
		return this.apiService.post<{ snapshotHash: string; translations: { [key: string]: string } }>(`translation/translate-page`, {
			employerId,
			fromLanguage,
			toLanguage,
			pagePath,
			items
		});
	}
}